import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Calendar,
  IndianRupee,
  AlertTriangle,
  Clock,
  CheckCircle,
  FileText,
  History,
  Filter,
  Save,
  ArrowUpDown,
  Plus,
  Receipt,
  GripVertical,
  ArrowUp,
  ArrowDown,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

// Sortable Row Component
const SortableRow = ({ item, index, isSelected, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.item_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };

  return (
    <TableRow 
      ref={setNodeRef} 
      style={style} 
      className={`${isDragging ? 'bg-blue-100 shadow-lg' : ''} ${isSelected ? 'bg-blue-50' : ''}`}
      data-testid={`pipeline-row-${item.item_id}`}
    >
      <TableCell className="w-12">
        <div className="flex items-center gap-2">
          <button 
            {...attributes} 
            {...listeners} 
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded touch-none"
            data-testid={`drag-handle-${item.item_id}`}
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </button>
          <span className="text-xs font-bold text-gray-500 w-4">{index + 1}</span>
        </div>
      </TableCell>
      {children}
    </TableRow>
  );
};

const ExpensePipeline = ({ user }) => {
  const [pipeline, setPipeline] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [vendors, setVendors] = useState([]);
  
  // Filters
  const [filterPriority, setFilterPriority] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  // Add Bill Dialog
  const [showAddBill, setShowAddBill] = useState(false);
  const [addingBill, setAddingBill] = useState(false);
  const [newBill, setNewBill] = useState({
    vendor_id: '',
    bill_number: '',
    bill_date: new Date().toISOString().split('T')[0],
    amount: '',
    remarks: ''
  });
  
  // Add Liability Dialog
  const [showAddLiability, setShowAddLiability] = useState(false);
  const [addingLiability, setAddingLiability] = useState(false);
  const [newLiability, setNewLiability] = useState({
    vendor_id: '',
    amount: '',
    remarks: ''
  });
  
  // Edit dialog
  const [editItem, setEditItem] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [editForm, setEditForm] = useState({
    priority: 2,
    planned_date: '',
    planned_amount: '',
    payment_method: '',
    notes: ''
  });
  
  // Bulk selection
  const [selectedItems, setSelectedItems] = useState({});
  const [bulkDate, setBulkDate] = useState('');
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkScheduling, setBulkScheduling] = useState(false);

  const fetchPipeline = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = {};
      if (filterPriority && filterPriority !== 'all') params.priority = filterPriority;
      if (filterVendor && filterVendor !== 'all') params.vendor_id = filterVendor;
      if (filterDateFrom) params.planned_date_from = filterDateFrom;
      if (filterDateTo) params.planned_date_to = filterDateTo;
      
      const response = await axios.get(`${API}/expense-pipeline`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setPipeline(response.data.items || []);
      setSummary(response.data.summary || {});
      setLastRefresh(new Date());
    } catch (error) {
      toast.error('Failed to fetch pipeline');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors');
    }
  };

  useEffect(() => {
    fetchPipeline();
    fetchVendors();
  }, [fetchPipeline, filterPriority, filterVendor, filterDateFrom, filterDateTo]);

  // Auto-refresh every 45 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchPipeline(true), 45000);
    return () => clearInterval(interval);
  }, [fetchPipeline]);

  const handleManualRefresh = () => {
    fetchPipeline(true);
    toast.success('Data refreshed!');
  };

  // Quick Add Bill Handler
  const handleAddBill = async (e) => {
    e.preventDefault();
    if (addingBill) return; // Prevent double-click
    
    try {
      setAddingBill(true);
      const token = localStorage.getItem('token');
      
      // Create bill
      const formData = new FormData();
      formData.append('vendor_id', newBill.vendor_id);
      formData.append('bill_number', newBill.bill_number);
      formData.append('bill_date', newBill.bill_date);
      formData.append('amount', newBill.amount);
      formData.append('remarks', newBill.remarks || '');
      
      const createRes = await axios.post(`${API}/purchase-bills`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const billId = createRes.data.id;
      
      // Submit for approval
      await axios.post(`${API}/purchase-bills/${billId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If admin, GM, or accountant, auto-approve
      if (['admin', 'general_manager', 'accountant'].includes(user?.role)) {
        await axios.post(`${API}/purchase-bills/${billId}/approve`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bill added and approved - now in pipeline');
      } else {
        toast.success('Bill added - awaiting approval');
      }
      
      setShowAddBill(false);
      setNewBill({ vendor_id: '', bill_number: '', bill_date: new Date().toISOString().split('T')[0], amount: '', remarks: '' });
      fetchPipeline();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add bill'));
    } finally {
      setAddingBill(false);
    }
  };

  // Quick Add Liability Handler
  const handleAddLiability = async (e) => {
    e.preventDefault();
    if (addingLiability) return; // Prevent double-click
    
    try {
      setAddingLiability(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/opening-liabilities`, {
        vendor_id: newLiability.vendor_id,
        amount: parseFloat(newLiability.amount),
        remarks: newLiability.remarks
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Opening liability added to pipeline');
      setShowAddLiability(false);
      setNewLiability({ vendor_id: '', amount: '', remarks: '' });
      fetchPipeline();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add liability'));
    } finally {
      setAddingLiability(false);
    }
  };

  const openEditDialog = (item) => {
    setEditItem(item);
    setEditForm({
      priority: item.priority || 2,
      planned_date: item.planned_date || '',
      planned_amount: item.planned_amount || item.pending_amount || '',
      payment_method: item.payment_method || '',
      notes: item.notes || ''
    });
    setEditDialog(true);
  };

  const handleSaveSchedule = async () => {
    if (savingSchedule) return; // Prevent double-click
    
    try {
      setSavingSchedule(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expense-pipeline/schedule`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          item_type: editItem.item_type,
          item_id: editItem.item_id,
          priority: editForm.priority,
          planned_date: editForm.planned_date || null,
          planned_amount: editForm.planned_amount || null,
          payment_method: editForm.payment_method || null,
          notes: editForm.notes || null
        }
      });
      toast.success('Schedule saved');
      setEditDialog(false);
      fetchPipeline();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save schedule'));
    } finally {
      setSavingSchedule(false);
    }
  };

  // Quick priority change (inline)
  const handleQuickPriorityChange = async (item, newPriority) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expense-pipeline/schedule`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          item_type: item.item_type,
          item_id: item.item_id,
          priority: newPriority,
          planned_date: item.planned_date || null,
          planned_amount: item.planned_amount || null,
          payment_method: item.payment_method || null,
          notes: item.notes || null
        }
      });
      toast.success(`Priority changed to ${newPriority === 1 ? 'High' : newPriority === 2 ? 'Medium' : 'Low'}`);
      fetchPipeline();
    } catch (error) {
      toast.error('Failed to change priority');
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handle drag end - reorder items
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = pipeline.findIndex(p => p.item_id === active.id);
      const newIndex = pipeline.findIndex(p => p.item_id === over.id);
      
      const newOrder = arrayMove(pipeline, oldIndex, newIndex);
      setPipeline(newOrder);
      
      // Save new order to backend
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/expense-pipeline/reorder`, 
          newOrder.map(p => p.item_id),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Payment order updated');
      } catch (error) {
        toast.error('Failed to save order');
        fetchPipeline(); // Revert on error
      }
    }
  };

  // Move item up/down manually
  const moveItem = async (itemId, direction) => {
    const currentIndex = pipeline.findIndex(p => p.item_id === itemId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= pipeline.length) return;
    
    const newOrder = arrayMove(pipeline, currentIndex, newIndex);
    setPipeline(newOrder);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expense-pipeline/reorder`, 
        newOrder.map(p => p.item_id),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payment order updated');
    } catch (error) {
      toast.error('Failed to save order');
      fetchPipeline();
    }
  };

  const handleBulkSchedule = async () => {
    if (bulkScheduling) return; // Prevent double-click
    
    const items = Object.keys(selectedItems)
      .filter(id => selectedItems[id])
      .map(itemId => {
        const item = pipeline.find(p => p.item_id === itemId);
        return {
          item_type: item.item_type,
          item_id: itemId,
          planned_date: bulkDate,
          planned_amount: item.pending_amount
        };
      });

    if (items.length === 0) {
      toast.error('Please select items to schedule');
      return;
    }

    try {
      setBulkScheduling(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expense-pipeline/bulk-schedule`, items, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Scheduled ${items.length} items`);
      setShowBulkDialog(false);
      setSelectedItems({});
      setBulkDate('');
      fetchPipeline();
    } catch (error) {
      toast.error('Failed to bulk schedule');
    } finally {
      setBulkScheduling(false);
    }
  };

  const toggleSelectAll = () => {
    if (Object.keys(selectedItems).length === pipeline.length) {
      setSelectedItems({});
    } else {
      const all = {};
      pipeline.forEach(p => { all[p.item_id] = true; });
      setSelectedItems(all);
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      1: { bg: 'bg-red-100 text-red-700', label: 'HIGH', icon: AlertTriangle },
      2: { bg: 'bg-yellow-100 text-yellow-700', label: 'MEDIUM', icon: Clock },
      3: { bg: 'bg-green-100 text-green-700', label: 'LOW', icon: CheckCircle }
    };
    const style = styles[priority] || styles[2];
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {style.label}
      </Badge>
    );
  };

  const getTypeBadge = (type, utilityType) => {
    if (type === 'bill') {
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <FileText className="w-3 h-3" />
          Bill
        </Badge>
      );
    } else if (type === 'utility') {
      return (
        <Badge variant="outline" className="bg-orange-50 text-orange-700 flex items-center gap-1">
          {utilityType === 'rent' ? '🏠' : '⚡'}
          {utilityType === 'rent' ? 'Rent' : 'PSPCL'}
        </Badge>
      );
    } else if (type === 'salary') {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 flex items-center gap-1">
          👤
          Salary
        </Badge>
      );
    } else {
      return (
        <Badge variant="outline" className="bg-purple-50 flex items-center gap-1">
          <History className="w-3 h-3" />
          Liability
        </Badge>
      );
    }
  };

  const selectedCount = Object.values(selectedItems).filter(Boolean).length;
  const selectedTotal = pipeline
    .filter(p => selectedItems[p.item_id])
    .reduce((sum, p) => sum + p.pending_amount, 0);

  const canAddBill = ['admin', 'general_manager', 'storekeeper'].includes(user?.role);
  const canAddLiability = user?.role === 'admin';

  return (
    <div className="space-y-6" data-testid="expense-pipeline-page">
      {/* Refresh Bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Last updated: {lastRefresh.toLocaleTimeString('en-IN')}
          {refreshing && <span className="ml-2 text-blue-500">• Refreshing...</span>}
        </p>
        <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={refreshing} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            Expense Pipeline
          </h1>
          <p className="text-gray-500 text-sm mt-1">Plan and prioritize pending payments</p>
        </div>
        <div className="flex gap-2">
          {/* Quick Add Bill */}
          {canAddBill && (
            <Dialog open={showAddBill} onOpenChange={setShowAddBill}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Receipt className="w-4 h-4 mr-2" />
                  Add Bill
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Quick Add Purchase Bill</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddBill} className="space-y-4">
                  <div>
                    <Label>Vendor *</Label>
                    <Select value={newBill.vendor_id} onValueChange={(v) => setNewBill({...newBill, vendor_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bill Number *</Label>
                      <Input 
                        value={newBill.bill_number} 
                        onChange={(e) => setNewBill({...newBill, bill_number: e.target.value})}
                        placeholder="INV-001"
                        required
                      />
                    </div>
                    <div>
                      <Label>Bill Date *</Label>
                      <Input 
                        type="date"
                        value={newBill.bill_date} 
                        onChange={(e) => setNewBill({...newBill, bill_date: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Amount *</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={newBill.amount} 
                      onChange={(e) => setNewBill({...newBill, amount: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Remarks</Label>
                    <Textarea 
                      value={newBill.remarks} 
                      onChange={(e) => setNewBill({...newBill, remarks: e.target.value})}
                      placeholder="Optional notes..."
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    {['admin', 'general_manager', 'accountant'].includes(user?.role)
                      ? 'Bill will be auto-approved and added to pipeline' 
                      : 'Bill will be submitted for approval'}
                  </p>
                  <Button type="submit" className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Bill
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {/* Quick Add Liability */}
          {canAddLiability && (
            <Dialog open={showAddLiability} onOpenChange={setShowAddLiability}>
              <DialogTrigger asChild>
                <Button variant="outline" className="bg-purple-50">
                  <History className="w-4 h-4 mr-2" />
                  Add Liability
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Quick Add Opening Liability</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddLiability} className="space-y-4">
                  <div>
                    <Label>Vendor *</Label>
                    <Select value={newLiability.vendor_id} onValueChange={(v) => setNewLiability({...newLiability, vendor_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount *</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={newLiability.amount} 
                      onChange={(e) => setNewLiability({...newLiability, amount: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Remarks</Label>
                    <Textarea 
                      value={newLiability.remarks} 
                      onChange={(e) => setNewLiability({...newLiability, remarks: e.target.value})}
                      placeholder="e.g., Balance from FY 2024-25"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Liability
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {selectedCount > 0 && (
            <Button onClick={() => setShowBulkDialog(true)}>
              <Calendar className="w-4 h-4 mr-2" />
              Schedule {selectedCount} Items
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <p className="text-sm text-red-600 font-medium">Total Pending</p>
            <p className="text-xl font-bold text-red-700">{formatCurrency(summary.total_pending)}</p>
            <p className="text-xs text-red-500">{summary.total_items} items</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4">
            <p className="text-sm text-orange-600 font-medium">High Priority</p>
            <p className="text-xl font-bold text-orange-700">{formatCurrency(summary.high_priority_amount)}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <p className="text-sm text-blue-600 font-medium">Scheduled</p>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(summary.total_scheduled)}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-sm text-purple-600 font-medium">Bills</p>
            <p className="text-xl font-bold text-purple-700">{summary.bills_count || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="pt-4">
            <p className="text-sm text-indigo-600 font-medium">Liabilities</p>
            <p className="text-xl font-bold text-indigo-700">{summary.liabilities_count || 0}</p>
          </CardContent>
        </Card>
        {(summary.utility_count || 0) > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4">
              <p className="text-sm text-amber-600 font-medium">Utility Bills</p>
              <p className="text-xl font-bold text-amber-700">{summary.utility_count || 0}</p>
            </CardContent>
          </Card>
        )}
        {(summary.salary_count || 0) > 0 && (
          <Card className="bg-teal-50 border-teal-200">
            <CardContent className="pt-4">
              <p className="text-sm text-teal-600 font-medium">Salaries</p>
              <p className="text-xl font-bold text-teal-700">{summary.salary_count || 0}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="1">High</SelectItem>
                  <SelectItem value="2">Medium</SelectItem>
                  <SelectItem value="3">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Vendor</Label>
              <Select value={filterVendor} onValueChange={setFilterVendor}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Planned From</Label>
              <Input
                type="date"
                className="w-36"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Planned To</Label>
              <Input
                type="date"
                className="w-36"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
              />
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setFilterPriority('all');
                setFilterVendor('all');
                setFilterDateFrom('');
                setFilterDateTo('');
              }}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Selection Summary */}
      {selectedCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center">
          <span className="text-blue-700">
            <strong>{selectedCount}</strong> items selected • Total: <strong>{formatCurrency(selectedTotal)}</strong>
          </span>
          <Button variant="ghost" size="sm" onClick={() => setSelectedItems({})}>
            Clear Selection
          </Button>
        </div>
      )}

      {/* Pipeline Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ArrowUpDown className="w-5 h-5" />
            Payment Schedule
            <span className="text-sm font-normal text-gray-500 ml-2">
              (Drag rows to reorder payment sequence)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : pipeline.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No pending bills or liabilities found
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead className="w-10">
                      <Checkbox 
                        checked={selectedCount === pipeline.length && pipeline.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Planned Date</TableHead>
                    <TableHead className="text-right">Planned Amt</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <SortableContext
                  items={pipeline.map(p => p.item_id)}
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {pipeline.map((item, index) => (
                      <SortableRow 
                        key={item.item_id} 
                        item={item} 
                        index={index}
                        isSelected={selectedItems[item.item_id]}
                      >
                        <TableCell>
                          <Checkbox 
                            checked={!!selectedItems[item.item_id]}
                            onCheckedChange={(checked) => {
                              setSelectedItems(prev => ({
                                ...prev,
                                [item.item_id]: checked
                              }));
                            }}
                          />
                        </TableCell>
                        <TableCell>{getTypeBadge(item.item_type, item.utility_type)}</TableCell>
                        <TableCell className="font-medium">{item.vendor_name}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {item.item_type === 'bill' ? (
                            <span>#{item.bill_number} • {item.bill_date}</span>
                          ) : item.item_type === 'utility' ? (
                            <span className="text-orange-600">{item.bill_number}</span>
                          ) : (
                            <span className="italic">Opening Liability</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatCurrency(item.pending_amount)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleQuickPriorityChange(item, 1)}
                              className={`w-6 h-6 rounded text-xs font-bold transition-all ${
                                item.priority === 1 
                                  ? 'bg-red-500 text-white ring-2 ring-red-300' 
                                  : 'bg-red-100 text-red-600 hover:bg-red-200'
                              }`}
                              title="High Priority"
                            >
                              H
                            </button>
                            <button
                              onClick={() => handleQuickPriorityChange(item, 2)}
                              className={`w-6 h-6 rounded text-xs font-bold transition-all ${
                                item.priority === 2 
                                  ? 'bg-yellow-500 text-white ring-2 ring-yellow-300' 
                                  : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                              }`}
                              title="Medium Priority"
                            >
                              M
                            </button>
                            <button
                              onClick={() => handleQuickPriorityChange(item, 3)}
                              className={`w-6 h-6 rounded text-xs font-bold transition-all ${
                                item.priority === 3 
                                  ? 'bg-green-500 text-white ring-2 ring-green-300' 
                                  : 'bg-green-100 text-green-600 hover:bg-green-200'
                              }`}
                              title="Low Priority"
                            >
                              L
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.planned_date ? (
                            <span className="text-blue-600 font-medium">
                              {new Date(item.planned_date).toLocaleDateString('en-IN')}
                            </span>
                          ) : (
                            <span className="text-gray-400">Not set</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.planned_amount ? (
                            <span className="text-green-600">{formatCurrency(item.planned_amount)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <div className="flex flex-col">
                              <button
                                onClick={() => moveItem(item.item_id, 'up')}
                                disabled={index === 0}
                                className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move up"
                              >
                                <ArrowUp className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => moveItem(item.item_id, 'down')}
                                disabled={index === pipeline.length - 1}
                                className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move down"
                              >
                                <ArrowDown className="w-3 h-3" />
                              </button>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditDialog(item)}
                            >
                              <Calendar className="w-4 h-4 mr-1" />
                              Schedule
                            </Button>
                          </div>
                        </TableCell>
                      </SortableRow>
                    ))}
                  </TableBody>
                </SortableContext>
              </Table>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Edit Schedule Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Payment</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{editItem.vendor_name}</p>
                <p className="text-sm text-gray-500">
                  {editItem.item_type === 'bill' ? `Bill #${editItem.bill_number}` : 'Opening Liability'}
                </p>
                <p className="text-lg font-bold text-red-600 mt-1">
                  Pending: {formatCurrency(editItem.pending_amount)}
                </p>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select 
                  value={String(editForm.priority)} 
                  onValueChange={(v) => setEditForm({...editForm, priority: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">🔴 High Priority</SelectItem>
                    <SelectItem value="2">🟡 Medium Priority</SelectItem>
                    <SelectItem value="3">🟢 Low Priority</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Planned Payment Date</Label>
                <Input
                  type="date"
                  value={editForm.planned_date}
                  onChange={(e) => setEditForm({...editForm, planned_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Planned Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.planned_amount}
                  onChange={(e) => setEditForm({...editForm, planned_amount: e.target.value})}
                  placeholder={editItem.pending_amount}
                />
              </div>
              
              <div>
                <Label>Payment Method</Label>
                <Select 
                  value={editForm.payment_method} 
                  onValueChange={(v) => setEditForm({...editForm, payment_method: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Any notes for this payment..."
                />
              </div>
              
              <Button onClick={handleSaveSchedule} className="w-full">
                <Save className="w-4 h-4 mr-2" />
                Save Schedule
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Schedule Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Schedule Payments</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Schedule <strong>{selectedCount}</strong> items for payment on the same date.
            </p>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">Total Amount: {formatCurrency(selectedTotal)}</p>
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={bulkDate}
                onChange={(e) => setBulkDate(e.target.value)}
                required
              />
            </div>
            <Button onClick={handleBulkSchedule} className="w-full" disabled={!bulkDate}>
              <Calendar className="w-4 h-4 mr-2" />
              Schedule All
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensePipeline;
